import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import MainLayout from "@/components/MainLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { toast } from "sonner";
import { Save, MailCheck, MailX, Mail, Info, Plus, ArrowUpDown, ArrowUp, ArrowDown, SendHorizontal, Loader2, ChevronsUpDown, Check, Download, Upload } from "lucide-react";

export default function Recipients() {
  const [recipients, setRecipients] = useState([]);
  const [banners, setBanners] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [editedRows, setEditedRows] = useState({});
  const [saving, setSaving] = useState(false);

  // Filters
  const [filterBanner, setFilterBanner] = useState("");
  const [filterBrand, setFilterBrand] = useState("");

  // Sorting
  const [sortCol, setSortCol] = useState("");
  const [sortDir, setSortDir] = useState("asc");

  // Add recipient form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newBannerId, setNewBannerId] = useState("");
  const [adding, setAdding] = useState(false);
  const [bannerSearchOpen, setBannerSearchOpen] = useState(false);

  // Send now
  const [sendNowDialogOpen, setSendNowDialogOpen] = useState(false);
  const [sendNowTarget, setSendNowTarget] = useState(null);
  const [sendingNow, setSendingNow] = useState(false);

  // Import
  const [importing, setImporting] = useState(false);
  const fileInputRef = useCallback((node) => { if (node) node.value = ""; }, []);

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (filterBanner) params.banner_id = filterBanner;
      if (filterBrand) params.brand_id = filterBrand;

      const [recipientsRes, bannersRes, brandsRes] = await Promise.all([
        axios.get(`${API}/recipients`, { params }),
        axios.get(`${API}/banners`),
        axios.get(`${API}/brands`).catch(() => ({ data: [] })),
      ]);
      setRecipients(recipientsRes.data);
      setBanners(bannersRes.data);
      setBrands(brandsRes.data);
      setEditedRows({});
    } catch (error) {
      toast.error("Failed to load recipients");
    } finally {
      setLoading(false);
    }
  }, [filterBanner, filterBrand]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFieldChange = (index, field, value) => {
    const key = `${recipients[index].banner_id}-${index}`;
    setEditedRows(prev => ({
      ...prev,
      [key]: {
        ...recipients[index],
        ...(prev[key] || {}),
        [field]: value,
        _index: index,
      }
    }));
  };

  const getDisplayValue = (index, field) => {
    const key = `${recipients[index].banner_id}-${index}`;
    if (editedRows[key] && editedRows[key][field] !== undefined) {
      return editedRows[key][field];
    }
    return recipients[index][field];
  };

  const hasUnsavedChanges = Object.keys(editedRows).length > 0;

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const changesByBanner = {};
      for (const [, edit] of Object.entries(editedRows)) {
        const bannerId = edit.banner_id;
        if (!changesByBanner[bannerId]) {
          changesByBanner[bannerId] = recipients
            .filter(r => r.banner_id === bannerId)
            .map((r) => ({
              name: r.recipient_name,
              email: r.recipient_email,
              active: r.recipient_active,
              _origIdx: recipients.indexOf(r),
            }));
        }
      }
      for (const [, edit] of Object.entries(editedRows)) {
        const bannerId = edit.banner_id;
        const bannerRecipients = changesByBanner[bannerId];
        const origIdx = edit._index;
        const match = bannerRecipients.find(r => r._origIdx === origIdx);
        if (match) {
          if (edit.recipient_name !== undefined) match.name = edit.recipient_name;
          if (edit.recipient_email !== undefined) match.email = edit.recipient_email;
          if (edit.recipient_active !== undefined) match.active = edit.recipient_active;
        }
      }
      const promises = Object.entries(changesByBanner).map(([bannerId, recs]) =>
        axios.put(`${API}/recipients/banner/${bannerId}`, {
          banner_id: bannerId,
          recipients: recs.map(({ name, email, active }) => ({ name, email, active })),
        })
      );
      await Promise.all(promises);
      toast.success("Recipients updated successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleAddRecipient = async () => {
    if (!newEmail || !newBannerId) {
      toast.error("Email and Banner are required");
      return;
    }
    setAdding(true);
    try {
      await axios.post(`${API}/recipients/add`, { banner_id: newBannerId, name: newName, email: newEmail });
      toast.success("Recipient added");
      setNewName("");
      setNewEmail("");
      setNewBannerId("");
      setShowAddForm(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add recipient");
    } finally {
      setAdding(false);
    }
  };

  const openSendNowDialog = (recipient) => {
    setSendNowTarget(recipient);
    setSendNowDialogOpen(true);
  };

  const handleSendNow = async () => {
    if (!sendNowTarget) return;
    setSendingNow(true);
    try {
      await axios.post(`${API}/email-queue/send-now`, {
        banner_id: sendNowTarget.banner_id,
        recipient_email: sendNowTarget.recipient_email,
        recipient_name: sendNowTarget.recipient_name,
      });
      toast.success(`Email sent to ${sendNowTarget.recipient_email}`);
      setSendNowDialogOpen(false);
      setSendNowTarget(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSendingNow(false);
    }
  };

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
  };

  const handleDownloadCSV = async () => {
    try {
      const res = await fetch(`${API}/recipients/export`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "recipients.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch {
      toast.error("Failed to download CSV");
    }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/recipients/import`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  const getSortIcon = (col) => {
    if (sortCol !== col) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    return sortDir === "asc" ? <ArrowUp className="w-3 h-3 ml-1" /> : <ArrowDown className="w-3 h-3 ml-1" />;
  };

  const sortedRecipients = [...recipients].sort((a, b) => {
    if (!sortCol) return 0;
    const valA = (a[sortCol] ?? "").toString().toLowerCase();
    const valB = (b[sortCol] ?? "").toString().toLowerCase();
    if (sortCol === "emails_sent" || sortCol === "open_rate" || sortCol === "click_rate") {
      return sortDir === "asc" ? (a[sortCol] || 0) - (b[sortCol] || 0) : (b[sortCol] || 0) - (a[sortCol] || 0);
    }
    if (valA < valB) return sortDir === "asc" ? -1 : 1;
    if (valA > valB) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const getRateColor = (rate, highThreshold) => {
    if (rate >= highThreshold) return "text-emerald-400";
    if (rate > 0) return "text-amber-400";
    return "text-gray-500";
  };


  const getStatusBadge = (status, reason) => {
    if (status === "active") {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-500/15 text-emerald-400" data-testid="status-active">
          <MailCheck className="w-3 h-3" />
          Will receive
        </span>
      );
    }
    const colorMap = {
      blocked_lottery: "bg-red-500/15 text-red-400",
      blocked_banner: "bg-amber-500/15 text-amber-400",
      blocked_recipient: "bg-gray-500/15 text-gray-400",
    };
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${colorMap[status] || colorMap.blocked_recipient}`} data-testid={`status-${status}`} title={reason}>
        <MailX className="w-3 h-3" />
        {reason}
      </span>
    );
  };

  const formatDate = (iso) => {
    if (!iso) return "-";
    try {
      return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return iso; }
  };

  const activeCount = recipients.filter(r => r.effective_status === "active").length;

  const uniqueBanners = [...new Map(recipients.map(r => [r.banner_id, { id: r.banner_id, name: r.banner_name }])).values()];
  const uniqueBrands = [...new Map(recipients.filter(r => r.brand_id).map(r => [r.brand_id, { id: r.brand_id, name: r.brand_name }])).values()];

  const selectedBannerName = banners.find(b => b.id === newBannerId)?.name || "";

  return (
    <MainLayout title="Recipients" subtitle="View and manage all email recipients across banners">
      <div className="space-y-6">
        {/* Top bar */}
        <div className="flex justify-between items-start flex-wrap gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="bg-white/5 border border-white/10 rounded-lg px-4 py-3">
              <span className="text-sm text-gray-400">Total: </span>
              <span className="text-sm font-medium text-white" data-testid="total-recipients-count">{recipients.length}</span>
            </div>
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-4 py-3">
              <span className="text-sm text-emerald-400">Active: </span>
              <span className="text-sm font-medium text-emerald-300" data-testid="active-recipients-count">{activeCount}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasUnsavedChanges && (
              <Button onClick={handleSaveAll} disabled={saving} className="bg-[#10B981] hover:bg-[#059669] text-white" data-testid="save-recipients-btn">
                <Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "Save Changes"}
              </Button>
            )}
            <Button onClick={handleDownloadCSV} variant="outline" className="border-white/10 text-gray-300 hover:text-white" data-testid="download-csv-btn">
              <Download className="w-4 h-4 mr-2" />Export CSV
            </Button>
            <label className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-white/10 text-sm text-gray-300 hover:text-white hover:bg-white/5 cursor-pointer transition-colors" data-testid="import-csv-btn">
              <Upload className="w-4 h-4" />{importing ? "Importing..." : "Import CSV"}
              <input type="file" accept=".csv" onChange={handleImportCSV} className="hidden" disabled={importing} />
            </label>
            <Button onClick={() => setShowAddForm(!showAddForm)} variant="outline" className="border-white/10 text-gray-300 hover:text-white" data-testid="add-recipient-toggle-btn">
              <Plus className="w-4 h-4 mr-2" />Add Recipient
            </Button>
          </div>
        </div>

        {/* Add recipient form */}
        {showAddForm && (
          <div className="bg-white/5 border border-white/10 rounded-xl p-4">
            <h4 className="text-sm font-medium text-white mb-3">Add New Recipient</h4>
            <div className="flex items-end gap-3 flex-wrap">
              <div className="flex-1 min-w-[150px]">
                <label className="text-xs text-gray-400 block mb-1">Name</label>
                <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="John Doe" className="bg-[#1a1a24] border-white/10 text-white h-9" data-testid="new-recipient-name" />
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs text-gray-400 block mb-1">Email *</label>
                <Input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="john@example.com" type="email" className="bg-[#1a1a24] border-white/10 text-white h-9" data-testid="new-recipient-email" />
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs text-gray-400 block mb-1">Banner *</label>
                <Popover open={bannerSearchOpen} onOpenChange={setBannerSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" aria-expanded={bannerSearchOpen} className="w-full h-9 justify-between bg-[#1a1a24] border-white/10 text-sm font-normal hover:bg-white/5" data-testid="new-recipient-banner-select">
                      <span className={selectedBannerName ? "text-white" : "text-gray-500"}>
                        {selectedBannerName || "Select banner..."}
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[300px] p-0 bg-[#1a1a24] border-white/10" align="start">
                    <Command className="bg-[#1a1a24]">
                      <CommandInput placeholder="Search banners..." className="text-white" />
                      <CommandList>
                        <CommandEmpty className="text-gray-500">No banner found.</CommandEmpty>
                        <CommandGroup>
                          {banners.map(b => (
                            <CommandItem
                              key={b.id}
                              value={b.name}
                              onSelect={() => { setNewBannerId(b.id); setBannerSearchOpen(false); }}
                              className="text-gray-300 hover:text-white data-[selected=true]:bg-white/10 data-[selected=true]:text-white"
                            >
                              <Check className={`mr-2 h-4 w-4 ${newBannerId === b.id ? "opacity-100 text-[#10B981]" : "opacity-0"}`} />
                              {b.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <Button onClick={handleAddRecipient} disabled={adding} className="bg-[#10B981] hover:bg-[#059669] text-white h-9" data-testid="add-recipient-submit-btn">
                {adding ? "Adding..." : "Add"}
              </Button>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap mt-6">
          <select value={filterBanner} onChange={(e) => setFilterBanner(e.target.value)} className="h-9 px-3 rounded-lg bg-[#1a1a24] border border-white/10 text-sm text-gray-300 focus:border-[#10B981] outline-none" data-testid="filter-banner-select">
            <option value="">All Banners</option>
            {uniqueBanners.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <select value={filterBrand} onChange={(e) => setFilterBrand(e.target.value)} className="h-9 px-3 rounded-lg bg-[#1a1a24] border border-white/10 text-sm text-gray-300 focus:border-[#10B981] outline-none" data-testid="filter-brand-select">
            <option value="">All Brands</option>
            {uniqueBrands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          {(filterBanner || filterBrand) && (
            <Button variant="ghost" size="sm" onClick={() => { setFilterBanner(""); setFilterBrand(""); }} className="text-gray-400 hover:text-white text-xs" data-testid="clear-filters-btn">
              Clear filters
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#10B981]"></div>
          </div>
        ) : recipients.length === 0 ? (
          <div className="bg-white/5 border border-white/10 rounded-xl p-12 text-center">
            <Mail className="w-12 h-12 text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No recipients yet</h3>
            <p className="text-gray-400">Add recipients using the button above or in the Banner Editor.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 rounded-lg px-4 py-3 mt-6">
              <Info className="w-4 h-4 text-blue-400 shrink-0" />
              <p className="text-xs text-blue-300">
                Edit inline, then click <strong>Save Changes</strong>. Click column headers to sort. Use <strong>Send Now</strong> to immediately email a recipient.
              </p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl overflow-x-auto mt-6">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-gray-400 min-w-[150px] cursor-pointer select-none" onClick={() => handleSort("recipient_name")}>
                      <span className="inline-flex items-center">Name{getSortIcon("recipient_name")}</span>
                    </TableHead>
                    <TableHead className="text-gray-400 min-w-[200px] cursor-pointer select-none" onClick={() => handleSort("recipient_email")}>
                      <span className="inline-flex items-center">Email{getSortIcon("recipient_email")}</span>
                    </TableHead>
                    <TableHead className="text-gray-400 cursor-pointer select-none" onClick={() => handleSort("banner_name")}>
                      <span className="inline-flex items-center">Banner{getSortIcon("banner_name")}</span>
                    </TableHead>
                    <TableHead className="text-gray-400 cursor-pointer select-none" onClick={() => handleSort("brand_name")}>
                      <span className="inline-flex items-center">Brand{getSortIcon("brand_name")}</span>
                    </TableHead>
                    <TableHead className="text-gray-400 cursor-pointer select-none" onClick={() => handleSort("added_at")}>
                      <span className="inline-flex items-center">Date Added{getSortIcon("added_at")}</span>
                    </TableHead>
                    <TableHead className="text-gray-400 cursor-pointer select-none text-center" onClick={() => handleSort("emails_sent")}>
                      <span className="inline-flex items-center">Sent{getSortIcon("emails_sent")}</span>
                    </TableHead>
                    <TableHead className="text-gray-400 cursor-pointer select-none text-center" onClick={() => handleSort("open_rate")}>
                      <span className="inline-flex items-center">Open %{getSortIcon("open_rate")}</span>
                    </TableHead>
                    <TableHead className="text-gray-400 cursor-pointer select-none text-center" onClick={() => handleSort("click_rate")}>
                      <span className="inline-flex items-center">Click %{getSortIcon("click_rate")}</span>
                    </TableHead>
                    <TableHead className="text-gray-400 text-center">Active</TableHead>
                    <TableHead className="text-gray-400">Status</TableHead>
                    <TableHead className="text-gray-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedRecipients.map((r) => {
                    const origIdx = recipients.indexOf(r);
                    return (
                      <TableRow key={`${r.banner_id}-${origIdx}`} className="border-white/10 hover:bg-white/5" data-testid={`recipient-row-${origIdx}`}>
                        <TableCell>
                          <Input
                            value={getDisplayValue(origIdx, "recipient_name")}
                            onChange={(e) => handleFieldChange(origIdx, "recipient_name", e.target.value)}
                            className="bg-transparent border-white/10 text-white h-8 text-sm"
                            placeholder="Name"
                            data-testid={`recipient-name-${origIdx}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={getDisplayValue(origIdx, "recipient_email")}
                            onChange={(e) => handleFieldChange(origIdx, "recipient_email", e.target.value)}
                            className="bg-transparent border-white/10 text-white h-8 text-sm"
                            placeholder="email@example.com"
                            data-testid={`recipient-email-${origIdx}`}
                          />
                        </TableCell>
                        <TableCell className="text-sm">
                          <button
                            onClick={() => navigate(`/banner-editor/${r.banner_id}`)}
                            className="text-blue-400 hover:text-blue-300 hover:underline text-left"
                            data-testid={`banner-link-${origIdx}`}
                          >
                            {r.banner_name}
                          </button>
                        </TableCell>
                        <TableCell className="text-purple-300 text-sm">{r.brand_name || "-"}</TableCell>
                        <TableCell className="text-gray-400 text-sm">{formatDate(r.added_at)}</TableCell>
                        <TableCell className="text-center text-gray-300 text-sm" data-testid={`emails-sent-${origIdx}`}>{r.emails_sent || 0}</TableCell>
                        <TableCell className="text-center text-sm" data-testid={`open-rate-${origIdx}`}>
                          {r.emails_sent > 0 ? (
                            <span className={getRateColor(r.open_rate, 50)}>
                              {r.open_rate}%
                            </span>
                          ) : <span className="text-gray-600">-</span>}
                        </TableCell>
                        <TableCell className="text-center text-sm" data-testid={`click-rate-${origIdx}`}>
                          {r.emails_sent > 0 ? (
                            <span className={getRateColor(r.click_rate, 20)}>
                              {r.click_rate}%
                            </span>
                          ) : <span className="text-gray-600">-</span>}
                        </TableCell>
                        <TableCell className="text-center">
                          <Switch
                            checked={getDisplayValue(origIdx, "recipient_active")}
                            onCheckedChange={(checked) => handleFieldChange(origIdx, "recipient_active", checked)}
                            data-testid={`recipient-toggle-${origIdx}`}
                          />
                        </TableCell>
                        <TableCell>{getStatusBadge(r.effective_status, r.effective_reason)}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openSendNowDialog(r)}
                            className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 text-xs"
                            data-testid={`send-now-${origIdx}`}
                          >
                            <SendHorizontal className="w-3 h-3 mr-1" />
                            Send Now
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </div>

      {/* Send Now Confirmation Dialog */}
      <AlertDialog open={sendNowDialogOpen} onOpenChange={setSendNowDialogOpen}>
        <AlertDialogContent className="bg-[#12121a] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Send Email Now</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              This will immediately send the banner email to <strong className="text-white">{sendNowTarget?.recipient_name || sendNowTarget?.recipient_email}</strong> ({sendNowTarget?.recipient_email}) for banner <strong className="text-white">{sendNowTarget?.banner_name}</strong>, regardless of lottery, banner, or recipient delivery settings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/10 text-white hover:bg-white/5" data-testid="send-now-cancel-btn">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSendNow}
              disabled={sendingNow}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="send-now-confirm-btn"
            >
              {sendingNow ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
              ) : (
                <><SendHorizontal className="w-4 h-4 mr-2" />Send Now</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MainLayout>
  );
}
